/**
 * Live gestures (W1-09 state-store; ARCHITECTURE.md §5.3): drags, scrubs and generator previews.
 *
 * Every `update(build)` runs `build` in a fresh TxDraft over the BASE document (the state before the gesture): the
 * previous pending ops are reverted first, the new ones applied, and one ChangeSet (source 'gesture') announces the
 * net change, so preview, timeline and inspector follow in real time. Nothing reaches history, the journal or the
 * workers until `commit`, which records ONE Txn with the composed ops; `cancel` (Esc) applies the inverse
 * (source 'gestureCancel') and leaves no trace.
 *
 * Ids: the draft of every update starts from the base id counter, so records created by a gesture keep the same
 * ids from one update to the next (stable ghosts, stable selection). The counter op itself is held back while the
 * gesture is live and applied only once the commit is accepted, which is why a cancelled gesture, or a commit the
 * store refuses, does not even move the counter.
 *
 * A failing update (`tx.fail`, an invariant violation, or an exception) keeps the previous state: the last valid
 * position stays on screen and the failure reason is returned (or the exception rethrown).
 */

import type { MessageRef } from '../core/util/types';
import { Ops } from '../core/ops/ops';
import type {
  DirtyScopes,
  IdSeqOp,
  Op,
  SelectionSnapshot,
  Txn,
  TxBuilder,
  TxDraft,
  TxDraftResult,
  TxnFocus,
} from '../core/ops/types';
import { m } from './state.messages';
import type { Gesture, TxnMeta, TxnOutcome, VersionTopic } from './types';

/** Ops applied to the container, with what they touched (computed against the document before applying). */
export interface AppliedBatch {
  readonly ops: readonly Op[];
  readonly dirty: DirtyScopes;
  readonly topics: ReadonlySet<VersionTopic>;
}

/** What a gesture needs from its store (implemented by the DocStore). */
export interface GestureHost {
  /** A draft over the store's container. */
  draft(sel: SelectionSnapshot): TxDraft;
  /** Runs `fn` with the store marked as building (nested transactions are refused). */
  building<T>(fn: () => T): T;
  /** Validates a finished draft's ops against the draft views: null when valid, else the failure reason. */
  check(draft: TxDraft, ops: readonly Op[]): MessageRef | null;
  /** Applies ops to the container without announcing them (dirty scopes and topics are recorded for `publish`). */
  applySilently(ops: readonly Op[]): AppliedBatch;
  /** Announces batches applied since the last publish as one ChangeSet (and bumps their version topics). */
  publish(source: 'gesture' | 'gestureCancel', batches: readonly AppliedBatch[]): void;
  /**
   * Commits ops that are already applied (except `idSeq`, applied here once the commit is accepted) as one history
   * transaction. Returns null when the commit was refused (strict invariant check); the store has then reverted the
   * gesture through `revert`, and the id counter was never raised.
   */
  commitApplied(
    meta: TxnMeta,
    ops: readonly Op[],
    selBefore: SelectionSnapshot,
    selection: Partial<SelectionSnapshot> | null,
    focus: TxnFocus | undefined,
    revert: () => void,
  ): Txn | null;
  /** Applies a selection change through the selection bridge. */
  applySelection(sel: SelectionSnapshot): void;
  /** The gesture ended (committed, cancelled or detached). */
  release(g: LiveGesture): void;
}

type GestureState = 'active' | 'committed' | 'cancelled' | 'detached';

export class LiveGesture implements Gesture {
  readonly meta: TxnMeta;
  readonly selBefore: SelectionSnapshot;
  private readonly host: GestureHost;
  /** Ops currently applied to the container on behalf of the gesture (no idSeq op). */
  private pending: readonly Op[] = [];
  private idSeqNext: number | null = null;
  private last: TxDraftResult | null = null;
  private state: GestureState = 'active';

  constructor(host: GestureHost, meta: TxnMeta, selBefore: SelectionSnapshot) {
    this.host = host;
    this.meta = meta;
    this.selBefore = selBefore;
  }

  get active(): boolean {
    return this.state === 'active';
  }

  /** Number of ops currently applied on behalf of the gesture. */
  get pendingOps(): number {
    return this.pending.length;
  }

  update<R>(build: (tx: TxBuilder) => R): TxnOutcome<R> {
    if (this.state !== 'active') return { ok: false, reason: { key: m.gestureEnded } };
    const host = this.host;
    const previous = this.pending;
    const reverted = previous.length > 0 ? host.applySilently(Ops.invertAll(previous)) : null;
    this.pending = [];
    // Undoes the silent revert: listeners never saw the intermediate state, so nothing is announced.
    const restore = (): void => {
      if (reverted === null) return;
      host.applySilently(previous);
      this.pending = previous;
    };

    const draft = host.draft(this.selBefore);
    let r: { readonly ok: true; readonly value: R } | { readonly ok: false; readonly reason: MessageRef };
    try {
      r = host.building(() => draft.run(build));
    } catch (e) {
      restore();
      throw e;
    }
    if (!r.ok) {
      restore();
      return { ok: false, reason: r.reason };
    }
    const res = draft.finish();
    const reason = host.check(draft, res.ops);
    if (reason !== null) {
      restore();
      return { ok: false, reason };
    }

    const live: Op[] = [];
    let idSeqNext: number | null = null;
    for (const op of res.ops) {
      if (op.o === 'idSeq') idSeqNext = idSeqNext === null ? op.next : Math.max(idSeqNext, op.next);
      else live.push(op);
    }
    const applied = live.length > 0 ? host.applySilently(live) : null;
    const batches: AppliedBatch[] = [];
    if (reverted !== null) batches.push(reverted);
    if (applied !== null) batches.push(applied);
    if (batches.length > 0) host.publish('gesture', batches);
    this.pending = live;
    this.idSeqNext = idSeqNext;
    this.last = res;
    return { ok: true, value: r.value, txn: null, notes: res.notes };
  }

  commit(): Txn | null {
    if (this.state !== 'active') return null;
    this.state = 'committed';
    this.host.release(this);
    const last = this.last;
    const selection = last === null ? null : last.selection;
    if (this.pending.length === 0) {
      if (selection !== null) this.host.applySelection({ ...this.selBefore, ...selection });
      return null;
    }
    const raw: Op[] = [...this.pending];
    if (this.idSeqNext !== null) raw.push({ o: 'idSeq', next: this.idSeqNext } satisfies IdSeqOp);
    const ops = Ops.compose(raw);
    const pending = this.pending;
    this.pending = [];
    const focus = (last === null ? null : last.focus) ?? this.meta.focus;
    return this.host.commitApplied(this.meta, ops, this.selBefore, selection, focus, () => {
      this.host.publish('gestureCancel', [this.host.applySilently(Ops.invertAll(pending))]);
    });
  }

  cancel(): void {
    if (this.state !== 'active') return;
    this.state = 'cancelled';
    this.host.release(this);
    const pending = this.pending;
    this.pending = [];
    this.idSeqNext = null;
    if (pending.length > 0) this.host.publish('gestureCancel', [this.host.applySilently(Ops.invertAll(pending))]);
  }

  /** The document was replaced under the gesture (store `load`): it ends without touching the new document. */
  detach(): void {
    if (this.state !== 'active') return;
    this.state = 'detached';
    this.pending = [];
    this.idSeqNext = null;
    this.host.release(this);
  }
}
